import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useStorageUpload } from '../../lib/upload'
import { formatDate } from '../../lib/format'
import type { ComplianceChecklist, ComplianceOnboardingResponse, ComplianceProgress } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, Input, PageLoader, Textarea,
} from '../../components/ui'
import { CheckIcon } from '../../components/icons'
import './onboarding.css'

type StepKey = 'staff' | 'safety_ack' | 'tc_ack' | 'license' | 'safety_officer' | 'equipment' | 'review'

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'staff', label: 'Staff' },
  { key: 'safety_ack', label: 'Safety' },
  { key: 'tc_ack', label: 'Terms' },
  { key: 'license', label: 'Licence' },
  { key: 'safety_officer', label: 'Safety officer' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'review', label: 'Review' },
]

function itemDone(key: StepKey, progress: ComplianceProgress): boolean {
  if (key === 'review') return progress.is_complete
  return progress.items[key]
}

export default function Onboarding() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [step, setStep] = useState(0)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['leader', 'onboarding'],
    queryFn: () => api.get<ComplianceOnboardingResponse>('/leader/onboarding'),
  })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<ComplianceOnboardingResponse>('/leader/onboarding', body),
    onSuccess: (resp) => qc.setQueryData(['leader', 'onboarding'], resp),
  })

  const submit = useMutation({
    mutationFn: () => api.post<{ ok: boolean; checklist: ComplianceChecklist }>('/leader/onboarding/submit'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leader', 'onboarding'] })
      void qc.invalidateQueries({ queryKey: ['leader', 'dashboard'] })
    },
  })

  // Staff/equipment counts can change from other tabs (inviting a team member,
  // registering a truck) — refresh right before showing the final tally.
  useEffect(() => {
    if (STEPS[step]?.key === 'review') void refetch()
  }, [step, refetch])

  if (isLoading) return <PageLoader label="Loading your compliance checklist…" />

  if (isError || !data) {
    return (
      <>
        <PageHead />
        <Alert tone="danger">{error instanceof Error ? error.message : 'Could not load your compliance checklist.'}</Alert>
      </>
    )
  }

  const { checklist, progress, registration_status: status } = data

  if (status === 'approved') {
    return (
      <>
        <PageHead />
        <Card>
          <CardBody>
            <div className="onb-approved">
              <span className="onb-approved__icon"><CheckIcon size={26} /></span>
              <div>
                <div className="onb-approved__title">Your organisation is approved to operate</div>
                <p className="onb-approved__body">
                  You can register sites, log production and dispatch loads. If any of your
                  details change, contact SSM Zambia to update your compliance record.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </>
    )
  }

  const current = STEPS[step]
  const goBack = () => setStep((s) => Math.max(s - 1, 0))
  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1))
  const saveAndNext = (body: Record<string, unknown>) => save.mutate(body, { onSuccess: goNext })
  const completeCount = Object.values(progress.items).filter(Boolean).length

  return (
    <>
      <PageHead />

      {status === 'changes_requested' && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="warning">
            <strong>The admin requested changes.</strong>
            {checklist.review_notes ? ` ${checklist.review_notes}` : ''} Update the relevant step below and resubmit.
          </Alert>
        </div>
      )}
      {status === 'pending_approval' && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="info">
            Submitted{checklist.submitted_at ? ` on ${formatDate(checklist.submitted_at)}` : ''} and awaiting admin
            approval. You can still update details below if anything needs to change.
          </Alert>
        </div>
      )}

      <div className="onb-wizard">
        <ol className="onb-tracker">
          {STEPS.map((s, i) => {
            const done = itemDone(s.key, progress)
            const active = i === step
            return (
              <li
                key={s.key}
                className={`onb-tracker__item${active ? ' onb-tracker__item--active' : ''}${done ? ' onb-tracker__item--done' : ''}`}
              >
                <button type="button" className="onb-tracker__btn" onClick={() => setStep(i)}>
                  <span className="onb-tracker__dot">{done ? <CheckIcon size={11} /> : i + 1}</span>
                  <span className="onb-tracker__label">{s.label}</span>
                </button>
              </li>
            )
          })}
        </ol>

        <Card>
          <CardHeader
            title={`Step ${step + 1} of ${STEPS.length} — ${current.label}`}
            sub={`${completeCount} of 6 requirements complete`}
          />
          <CardBody>
            {save.isError && (
              <div style={{ marginBottom: 'var(--sp-4)' }}>
                <Alert tone="danger">
                  {save.error instanceof ApiError ? save.error.message : 'Could not save that step.'}
                </Alert>
              </div>
            )}

            {current.key === 'staff' && (
              <StaffStep progress={progress} onBack={step > 0 ? goBack : undefined} onNext={goNext} />
            )}
            {current.key === 'safety_ack' && (
              <SafetyAckStep checklist={checklist} saving={save.isPending} onBack={goBack} onSave={saveAndNext} />
            )}
            {current.key === 'tc_ack' && (
              <TcAckStep
                checklist={checklist}
                royaltyTruckload={data.royalty_per_truckload_zmw ?? 100}
                royaltyTruck={data.royalty_per_truck_zmw ?? 200}
                cooperativeShare={data.cooperative_share_per_load_zmw ?? 500}
                communityShare={data.community_share_per_load_zmw ?? 200}
                farmerShare={data.farmer_share_per_load_zmw ?? 200}
                totalPerLoad={data.total_per_load_zmw ?? 1000}
                saving={save.isPending}
                onBack={goBack}
                onSave={saveAndNext}
              />
            )}
            {current.key === 'license' && (
              <LicenseStep
                checklist={checklist} orgId={user?.org_id ?? ''}
                saving={save.isPending} onBack={goBack} onSave={saveAndNext}
              />
            )}
            {current.key === 'safety_officer' && (
              <SafetyOfficerStep
                checklist={checklist} orgId={user?.org_id ?? ''}
                saving={save.isPending} onBack={goBack} onSave={saveAndNext}
              />
            )}
            {current.key === 'equipment' && (
              <EquipmentStep progress={progress} onBack={goBack} onNext={goNext} />
            )}
            {current.key === 'review' && (
              <ReviewStep
                progress={progress} status={status}
                onBack={goBack} onJump={setStep}
                onSubmit={() => submit.mutate()} submitting={submit.isPending}
                submitError={submit.error instanceof ApiError ? submit.error.message : undefined}
                submitted={submit.isSuccess}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </>
  )
}

function PageHead() {
  return (
    <div className="page-head">
      <div>
        <h1>Compliance setup</h1>
        <p className="page-head__sub">
          Complete every step below and submit for admin approval before you can register
          sites, log production or dispatch loads.
        </p>
      </div>
    </div>
  )
}

function StepNav({
  onBack, nextLabel = 'Continue', saving, disabled,
}: { onBack?: () => void; nextLabel?: string; saving?: boolean; disabled?: boolean }) {
  return (
    <div className="row" style={{ marginTop: 'var(--sp-2)' }}>
      {onBack && <Button type="button" variant="secondary" onClick={onBack} disabled={saving}>Back</Button>}
      <Button type="submit" loading={saving} disabled={disabled}>{nextLabel}</Button>
    </div>
  )
}

/* ── Step 1: Staff ─────────────────────────────────────────────────── */

function StaffStep({
  progress, onBack, onNext,
}: { progress: ComplianceProgress; onBack?: () => void; onNext: () => void }) {
  const met = progress.staff_count >= progress.staff_required
  return (
    <form onSubmit={(e) => { e.preventDefault(); onNext() }} className="stack">
      <p>
        SSM Zambia requires a minimum of <strong>{progress.staff_required} registered staff</strong> before
        your organisation can be approved to operate — this includes yourself and anyone you invite.
      </p>
      <div className={`onb-count${met ? ' onb-count--done' : ''}`}>
        <span className="onb-count__value">{progress.staff_count}</span>
        <span className="onb-count__label">of {progress.staff_required} minimum staff registered</span>
      </div>
      {!met && (
        <Alert tone="info">
          Invite more team members from the <Link to="/team">Team</Link> page, then come back and continue.
        </Alert>
      )}
      <StepNav onBack={onBack} nextLabel="Continue" />
    </form>
  )
}

/* ── Step 2: Safety acknowledgement ───────────────────────────────── */

const SAFETY_COMMITMENTS = [
  'Every worker wears the required PPE (helmet, boots, gloves, hi-vis) at all times on site.',
  'No one enters an unsupported pit, shaft or excavation.',
  'All workers are briefed on emergency procedures before their first shift.',
  'Machinery and equipment are inspected and maintained on a regular schedule.',
  'Incidents and hazards are reported and investigated, never hidden.',
  'A trained first-aider and a stocked first-aid kit are present on every active site.',
  'Blasting, where used, is carried out only by a qualified, licensed person.',
  'Workers under 18 are never engaged in hazardous mining tasks.',
]

function SafetyAckStep({
  checklist, saving, onBack, onSave,
}: {
  checklist: ComplianceChecklist; saving: boolean
  onBack: () => void; onSave: (body: Record<string, unknown>) => void
}) {
  const [checked, setChecked] = useState(!!checklist.safety_ack?.acknowledged)

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!checked) return
    onSave({ safety_ack: { acknowledged: true, accepted_at: new Date().toISOString() } })
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      <p>
        Our safety protocols are drawn from the SSM Zambia Safety Manual and the Occupational Health and
        Safety Act. By continuing, you commit your organisation to:
      </p>
      <ul className="onb-list">
        {SAFETY_COMMITMENTS.map((c) => <li key={c}>{c}</li>)}
      </ul>
      <label className="onb-check">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>I acknowledge these safety protocols and commit my organisation to complying with them.</span>
      </label>
      <StepNav onBack={onBack} nextLabel="Save & continue" saving={saving} disabled={!checked} />
    </form>
  )
}

/* ── Step 3: Community terms & royalty ────────────────────────────── */

function TcAckStep({
  checklist, royaltyTruckload, royaltyTruck, cooperativeShare, communityShare, farmerShare,
  totalPerLoad, saving, onBack, onSave,
}: {
  checklist: ComplianceChecklist; royaltyTruckload: number; royaltyTruck: number
  cooperativeShare: number; communityShare: number; farmerShare: number; totalPerLoad: number
  saving: boolean; onBack: () => void; onSave: (body: Record<string, unknown>) => void
}) {
  const [checked, setChecked] = useState(!!checklist.tc_ack?.accepted)

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!checked) return
    onSave({ tc_ack: { accepted: true, accepted_at: new Date().toISOString() } })
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      <p>
        Operating through SSM Zambia comes with a community-benefit commitment. Every load moved
        carries a levy that is split four ways — modelled on the Copperbelt Small Scale Miners
        cooperative&rsquo;s own published split:
      </p>
      <div className="onb-royalty">
        <div className="onb-royalty__row"><span>Cooperative</span><strong>K{cooperativeShare}</strong></div>
        <div className="onb-royalty__row"><span>Royalty</span><strong>K{royaltyTruckload}</strong></div>
        <div className="onb-royalty__row"><span>Community</span><strong>K{communityShare}</strong></div>
        <div className="onb-royalty__row"><span>Farmer</span><strong>K{farmerShare}</strong></div>
        <div className="onb-royalty__row onb-royalty__row--total"><span>Total per load</span><strong>K{totalPerLoad}</strong></div>
        <div className="onb-royalty__row"><span>Per truck operating on site</span><strong>K{royaltyTruck}</strong></div>
      </div>
      <p className="field__hint">
        These are SSM Zambia&rsquo;s current published community-benefit terms and may be updated from
        time to time — you will be notified of any change.
      </p>
      <label className="onb-check">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>I have read and understood these terms and agree, on behalf of my organisation, to be bound by them.</span>
      </label>
      <StepNav onBack={onBack} nextLabel="Save & continue" saving={saving} disabled={!checked} />
    </form>
  )
}

/* ── Step 4: Licence or partner ───────────────────────────────────── */

function LicenseStep({
  checklist, orgId, saving, onBack, onSave,
}: {
  checklist: ComplianceChecklist; orgId: string; saving: boolean
  onBack: () => void; onSave: (body: Record<string, unknown>) => void
}) {
  const [choice, setChoice] = useState<'own_license' | 'partner' | ''>(checklist.license_choice || '')
  const fileRef = useRef<HTMLInputElement>(null)
  const upload = useStorageUpload(`org_compliance/${orgId}/license`)
  const existingDoc = checklist.license_document?.url ? checklist.license_document : null

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!choice) return
    const body: Record<string, unknown> = { license_choice: choice }
    const file = fileRef.current?.files?.[0]
    if (choice === 'own_license') {
      if (file) {
        try {
          body.license_document = await upload.upload(file)
        } catch {
          return
        }
      } else if (!existingDoc) {
        upload.setError('Upload a copy of your mining licence to continue.')
        return
      }
    }
    onSave(body)
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      <p>Do you already hold a mining licence, or would you like SSM Zambia&rsquo;s compliance and safety programme to cover your operations as a partner site?</p>
      <div className="onb-radio-group">
        <label className={`onb-radio${choice === 'own_license' ? ' onb-radio--selected' : ''}`}>
          <input type="radio" name="license_choice" checked={choice === 'own_license'}
                 onChange={() => setChoice('own_license')} />
          <span>
            <strong>I have my own mining licence</strong>
            <span className="onb-radio__hint">Upload a copy for our records.</span>
          </span>
        </label>
        <label className={`onb-radio${choice === 'partner' ? ' onb-radio--selected' : ''}`}>
          <input type="radio" name="license_choice" checked={choice === 'partner'}
                 onChange={() => setChoice('partner')} />
          <span>
            <strong>I&rsquo;d like to partner with SSM Zambia</strong>
            <span className="onb-radio__hint">
              We handle PPE, training, site supervision and Ministry reporting under our compliance programme.
            </span>
          </span>
        </label>
      </div>

      {choice === 'own_license' && (
        <div className="stack" style={{ marginTop: 'var(--sp-2)' }}>
          {existingDoc?.name && <div className="field__hint">Currently on file: {existingDoc.name}</div>}
          <Input
            label={existingDoc ? 'Replace licence document (optional)' : 'Mining licence document'}
            type="file" ref={fileRef} accept="image/*,application/pdf"
          />
          {upload.progress !== null && <div className="field__hint">Uploading… {upload.progress}%</div>}
          {upload.error && <Alert tone="danger">{upload.error}</Alert>}
        </div>
      )}

      <StepNav onBack={onBack} nextLabel="Save & continue" saving={saving || upload.progress !== null} disabled={!choice} />
    </form>
  )
}

/* ── Step 5: Safety officer ────────────────────────────────────────── */

function SafetyOfficerStep({
  checklist, orgId, saving, onBack, onSave,
}: {
  checklist: ComplianceChecklist; orgId: string; saving: boolean
  onBack: () => void; onSave: (body: Record<string, unknown>) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const upload = useStorageUpload(`org_compliance/${orgId}/safety_officer`)
  const officer = checklist.safety_officer || {}
  const existingDoc = officer.qualification_document?.url ? officer.qualification_document : null

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const name = String(form.get('name') || '').trim()
    const qualification = String(form.get('qualification') || '').trim()
    if (!name || !qualification) return
    const officerBody: Record<string, unknown> = { name, qualification }
    const file = fileRef.current?.files?.[0]
    if (file) {
      try {
        officerBody.qualification_document = await upload.upload(file)
      } catch {
        return
      }
    } else if (!existingDoc) {
      upload.setError('Upload the safety officer’s qualification document to continue.')
      return
    }
    onSave({ safety_officer: officerBody })
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      <p>Every organisation must have a named, qualified safety officer responsible for site safety.</p>
      <Input label="Safety officer's full name" name="name" required defaultValue={officer.name} />
      <Textarea
        label="Qualification / certification" name="qualification" required
        defaultValue={officer.qualification}
        hint="e.g. a recognised occupational health & safety qualification, plus a first-aid certificate"
      />
      {existingDoc?.name && <div className="field__hint">Currently on file: {existingDoc.name}</div>}
      <Input
        label={existingDoc ? 'Replace qualification document (optional)' : 'Qualification document'}
        type="file" ref={fileRef} accept="image/*,application/pdf"
      />
      {upload.progress !== null && <div className="field__hint">Uploading… {upload.progress}%</div>}
      {upload.error && <Alert tone="danger">{upload.error}</Alert>}
      <StepNav onBack={onBack} nextLabel="Save & continue" saving={saving || upload.progress !== null} />
    </form>
  )
}

/* ── Step 6: Equipment ─────────────────────────────────────────────── */

function EquipmentStep({
  progress, onBack, onNext,
}: { progress: ComplianceProgress; onBack: () => void; onNext: () => void }) {
  const met = progress.items.equipment
  return (
    <form onSubmit={(e) => { e.preventDefault(); onNext() }} className="stack">
      <p>Register at least one truck or piece of equipment your organisation operates.</p>
      <div className={`onb-count${met ? ' onb-count--done' : ''}`}>
        <span className="onb-count__value">{met ? <CheckIcon size={22} /> : '—'}</span>
        <span className="onb-count__label">{met ? 'Equipment registered' : 'No equipment registered yet'}</span>
      </div>
      {!met && (
        <Alert tone="info">
          Register your trucks from the <Link to="/trucks">Fleet</Link> page or other equipment from the{' '}
          <Link to="/equipment">Equipment</Link> page, then come back and continue.
        </Alert>
      )}
      <StepNav onBack={onBack} nextLabel="Continue" />
    </form>
  )
}

/* ── Step 7: Review & submit ───────────────────────────────────────── */

const REVIEW_ROWS: { key: keyof ComplianceProgress['items']; label: string; stepIndex: number }[] = [
  { key: 'staff', label: 'Minimum staff registered', stepIndex: 0 },
  { key: 'safety_ack', label: 'Safety protocols acknowledged', stepIndex: 1 },
  { key: 'tc_ack', label: 'Community terms accepted', stepIndex: 2 },
  { key: 'license', label: 'Mining licence / partnership choice', stepIndex: 3 },
  { key: 'safety_officer', label: 'Safety officer on file', stepIndex: 4 },
  { key: 'equipment', label: 'Equipment registered', stepIndex: 5 },
]

function ReviewStep({
  progress, status, onBack, onJump, onSubmit, submitting, submitError, submitted,
}: {
  progress: ComplianceProgress; status: string
  onBack: () => void; onJump: (i: number) => void
  onSubmit: () => void; submitting: boolean; submitError?: string; submitted: boolean
}) {
  if (submitted) {
    return (
      <div className="onb-approved">
        <span className="onb-approved__icon"><CheckIcon size={26} /></span>
        <div>
          <div className="onb-approved__title">Submitted for approval</div>
          <p className="onb-approved__body">
            An SSM Zambia admin will review your checklist. You&rsquo;ll be notified once it&rsquo;s approved.
          </p>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (progress.is_complete) onSubmit() }} className="stack">
      <ul className="onb-review">
        {REVIEW_ROWS.map((r) => (
          <li key={r.key} className={`onb-review__row${progress.items[r.key] ? ' onb-review__row--done' : ''}`}>
            <span className="onb-review__tick">{progress.items[r.key] ? <CheckIcon size={12} /> : ''}</span>
            <span>{r.label}</span>
            <button type="button" className="onb-review__edit" onClick={() => onJump(r.stepIndex)}>Edit</button>
          </li>
        ))}
      </ul>
      {!progress.is_complete && (
        <Alert tone="warning">Complete every step above before you can submit for approval.</Alert>
      )}
      {submitError && <Alert tone="danger">{submitError}</Alert>}
      <StepNav
        onBack={onBack}
        nextLabel={status === 'changes_requested' || status === 'pending_approval' ? 'Resubmit for approval' : 'Submit for approval'}
        saving={submitting}
        disabled={!progress.is_complete}
      />
    </form>
  )
}
